import { labById, modelById, sourceById } from "../data/v1";
import type { Claim, Price, Workload } from "../types";

export const isOwnerReported = (claim: Claim) => {
  const model = modelById.get(claim.modelId);
  const source = sourceById.get(claim.sourceId);
  return Boolean(model && source && model.ownerId === source.publisherId);
};

export const reporterName = (claim: Claim) => {
  const source = sourceById.get(claim.sourceId);
  return source ? labById.get(source.publisherId)?.shortName ?? source.publisherId : "Unknown";
};

export const calculateWorkloadCost = (price: Price, workload: Workload) => {
  const cachedShare = Math.min(100, Math.max(0, workload.cachedPercent)) / 100;
  const inputMillions = Math.max(0, workload.inputTokens) / 1_000_000;
  const outputMillions = Math.max(0, workload.outputTokens) / 1_000_000;
  const cachedMillions = inputMillions * cachedShare;
  const uncachedMillions = inputMillions - cachedMillions;
  const cachedRate = price.cachedInput ?? price.input;
  const searchCost = price.webSearchPerThousand
    ? (Math.max(0, workload.webSearches) / 1_000) * price.webSearchPerThousand
    : 0;

  return (
    uncachedMillions * price.input +
    cachedMillions * cachedRate +
    outputMillions * price.output +
    searchCost
  );
};

export interface CrossSourcePair {
  benchmarkId: string;
  modelId: string;
  first: Claim;
  second: Claim;
  delta: number;
}

export const getCrossSourcePairs = (claims: Claim[]) => {
  const buckets = new Map<string, Claim[]>();
  claims.forEach((claim) => {
    const key = `${claim.benchmarkId}:${claim.modelId}`;
    buckets.set(key, [...(buckets.get(key) ?? []), claim]);
  });

  const pairs: CrossSourcePair[] = [];
  buckets.forEach((bucket) => {
    for (let index = 0; index < bucket.length; index += 1) {
      for (let compareIndex = index + 1; compareIndex < bucket.length; compareIndex += 1) {
        const first = bucket[index];
        const second = bucket[compareIndex];
        if (first.sourceId !== second.sourceId) {
          pairs.push({
            benchmarkId: first.benchmarkId,
            modelId: first.modelId,
            first,
            second,
            delta: Number((second.score - first.score).toFixed(2)),
          });
        }
      }
    }
  });
  return pairs;
};

export const formatUsd = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: value < 0.01 ? 4 : 2,
    maximumFractionDigits: value < 0.01 ? 4 : 2,
  }).format(value);
